<template>
  <div>
    <b-form-row class="mt-3 mb-2">
      <b-col cols="12">
        <b-button
          class="mb-2 mb-sm-0 mr-sm-2 btn-responsive"
          size="sm"
          variant="primary"
          @click="toggleSelectAll"
        >
          {{ selectAll ? 'Avmarkera alla rader' : 'Markera alla rader' }}
        </b-button>

        <b-button
          class="mb-2 mb-sm-0 btn-responsive"
          size="sm"
          variant="danger"
          :disabled="!hasValda"
          @click="excludeSelectedRows"
        >
          Exkludera valda rader
        </b-button>
        <label class="totalRowsStyle"> {{ getRowCount }} </label>
        <label class="excludedRowsStyle"> {{ numberOfExcludedRowsText }} </label>
      </b-col>
    </b-form-row>
    <b-table
      id="table-rows"
      :busy="isBusy"
      class="mb-2 mt-2"
      :bordered="true"
      :striped="true"
      :head-variant="'dark'"
      hover
      sort-icon-left
      :items="items"
      :fields="fields"
      :small="true"
      :responsive="true"
      primary-key="rowNum"
      :per-page="perPage"
      sort-compare-locale="sv"
      no-sort-reset
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      sort-direction="desc"
      :no-local-sorting="true"
      :current-page="currentPage"
      @row-clicked="rowClicked"
      @sort-changed="onSort"
    >
      <template
        v-if="sumVisible"
        v-slot:top-row
      >
        <td :colspan="sumArrayColspan">
          <div class="sumrowtop">
            <div
              v-for="(sum, iSum) in summaryArray"
              :key="iSum"
              class="sumrowdiv"
            >
              <span>{{ sum.name }}:</span>
            </div>
          </div>
        </td>
        <td
          v-for="(measure, iMeasure) in measuresInTable"
          :key="iMeasure"
          class="text-right"
          colspan="1"
        >
          <div
            v-for="(sum, iSum) in summaryArray"
            :key="iSum"
            class="sumrowdiv"
          >
            {{ sum.measures[measure.key] }}
          </div>
        </td>
      </template>
      <template v-slot:cell(valda)="row">
        <template v-if="row.value">
          <b-checkbox
            v-model="row.value"
            class="avoid-clicks"
            aria-hidden="true"
          />
          <span class="sr-only">Vald</span>
        </template>
        <template v-else>
          <b-checkbox
            v-model="row.value"
            class="avoid-clicks"
            aria-hidden="true"
          />
          <span class="sr-only">Inte vald</span>
        </template>
      </template>      
      <template v-slot:cell(title_code)="data">
        <div class="textSelect">
          {{ data.value }}
        </div>
      </template>
    </b-table>
    <b-pagination
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
    />
  </div>
</template>

<script>
import {
  dateToyymmddFormat,
  addDays,
  daysBetween,
} from '../../../assets/js/utils.js';
import { bus } from '../../../main';
import { mapGetters } from 'vuex';

export default {
  name: 'ResultTable',
  data() {
    return {
      isBusy: false,
      loadingSearchResults: false,
      selectAll: false,
      latestRowClicked: null,
      shiftKey: false,
      totalRows: null,
      currentPage: 1,
      perPage: 50
    };
  },
  created() {
    this.handleKeyEvents = this.handleKeyEvents.bind(this);
    document.addEventListener('keydown', this.handleKeyEvents);
    document.addEventListener('keyup', this.handleKeyEvents);

    bus.$on('searching', this.handleSearching);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeyEvents);
    document.removeEventListener('keyup', this.handleKeyEvents);
    bus.$off('searching', this.handleSearching);
  },
  computed: {
    sumVisible() {
      return this.$store.getters[`searchResults/getSumVisible`];
    },
    ...mapGetters('searchApiBody', ['getSelectedPeriod']),
    sortBy: {
      get() {
        return this.$store.state.searchApiBody.payload.sort_by;
      },
      set(value) {
        return value;
      },
    },
    sortDesc: {
      get() {
        return this.$store.state.searchApiBody.payload.sort_direction == 'DESC';
      },
      set(value) {
        return value;
      },
    },
    measuresInTable() {
      return this.$store.getters[`searchResults/getMeasuresInTable`];
    },
    summaryArray() {
      return this.$store.state.searchResults.summaryArray;
    },
    items() {
      return this.$store.state.searchResults.results;
    },
    fields() {
      return this.$store.state.searchResults.resultFields.filter(z => z.visible == true);
    },
    rows() {
      return this.$store.state.searchResults.results.length;
    },
    selectedRows() {
      return this.$store.state.searchResults.results.filter((item) => item.valda).length;
    },
    hasSelection() {
      return this.$store.state.reachApiBody.payload.title_codes.length > 0;
    },
    hasValda() {
      return this.$store.state.searchResults.results.some((item) => item.valda === true);
    },
    getRowCount() {
      const previousRowsAndCurrentRows = this.perPage * this.currentPage;
      if (previousRowsAndCurrentRows > this.rows) {
        return `${this.rows}/${this.rows}`;
      }
      return `${previousRowsAndCurrentRows}/${this.rows}`;
    },
    allRowsDisplayed() {
      return this.$store.state.searchResults.totalRowCount;
    },
    selectedColumns() {
      return this.$store.state.searchApiBody.payload.columns;
    },
    numberOfExcludedRowsText() {
      const numberOfExcludedRows = this.$store.state.searchResults.totalRowCount - this.rows;
      return numberOfExcludedRows > 0 ? `(${numberOfExcludedRows} exkluderade)` : "";
    },
    sumArrayColspan() {
      return this.fields.filter(f => f.tdClass !== "d-none" && !f.isMeasure).length;
    }
  },
  methods: {
    handleKeyEvents(e) {
      if (e.key === 'Shift') {
        this.shiftKey = e.type === 'keydown';
      }
    },
    handleSearching() {
      this.selectAll = false;
    },
    formatDate(date) {
      return dateToyymmddFormat(date);
    },
    rowClicked(item) {
      const currentIndex = this.$store.state.searchResults.results.findIndex(
        element => element.rowNum === item.rowNum
      );
      const rowSelected = this.$store.state.searchResults.results[currentIndex]?.valda;

      if (this.shiftKey && this.latestRowClicked !== null) {
        const lastClickedIndex = this.$store.state.searchResults.results.findIndex(
          element => element.rowNum === this.latestRowClicked
        );

        const minIndex = Math.min(currentIndex, lastClickedIndex);
        const maxIndex = Math.max(currentIndex, lastClickedIndex);
        
        // Check if we're selecting or deselecting based on the clicked row's state
        const targetState = !rowSelected;

        // Apply the same state to all rows in the range
        for (let i = minIndex; i <= maxIndex; i++) {
          const rowNum = this.$store.state.searchResults.results[i].rowNum;
          if (targetState) {
            this.$store.commit('searchResults/select', rowNum);
          } else {
            this.$store.commit('searchResults/deselect', rowNum);
          }
        }
      } else {
        // Single row toggle
        if (rowSelected) {
          this.$store.commit('searchResults/deselect', item.rowNum);
        } else {
          this.$store.commit('searchResults/select', item.rowNum);
        }
        
        this.latestRowClicked = item.rowNum;
      }

      this.updateReachApiBody();
    },
    async onSort(item) {
      this.isBusy = true;
      this.latestRowClicked = null;

      await Promise.all([
        this.$store.commit('searchApiBody/updateSortBy', item.sortBy),
        this.$store.commit('searchApiBody/updateSortDirection', item.sortDesc ? 'DESC' : 'ASC')
      ]);
      
      this.$emit('sort-changed', item);
      this.isBusy = false;
    },
    async excludeSelectedRows() {
      this.isBusy = true;
      
      const selectedRows = this.$store.state.searchResults.results
        .filter(item => item.valda)
        .map(item => item.rowNum);
      
      this.$store.commit('searchApiBody/updateExcludedRows', selectedRows);
      this.$store.commit('reachApiBody/emptyTitleCodes');
      
      this.$emit('exclude-rows');
      
      this.selectAll = false;
      this.isBusy = false;
    },
    toggleSelectAll() {
      this.latestRowClicked = null;
      this.selectAll = !this.selectAll;
      
      if (this.selectAll) {
        this.$store.commit('searchResults/selectAll');
      } else {
        this.$store.commit('searchResults/unselectAll');
      }

      this.updateReachApiBody();
    },
    updateReachApiBody() {
      let minDate = '';
      let maxDate = '';

      if (this.getSelectedPeriod === 'consumptionperiod') {
        minDate = this.$store.state.searchApiBody.payload.start_date;
        maxDate = this.$store.state.searchApiBody.payload.end_date;
      } else {
        const selectedItems = this.items.filter(e => e.valda);
        if (selectedItems.length === 0) {
          this.$store.commit('reachApiBody/updateTitleCodes', []);
          return;
        }
        
        const publishDates = selectedItems.map(e => new Date(e.published_at));
        maxDate = new Date(Math.max(...publishDates));
        minDate = new Date(Math.min(...publishDates));
      }

      const days = daysBetween(minDate, maxDate);
      let updateAutoChangeEndDate = false;

      // Max days to transfer to reach is 28
      if (days >= this.config_MaxReachDays) {
        maxDate = addDays(minDate, this.config_MaxReachDays - 1);
        updateAutoChangeEndDate = true;
      } else if (this.getSelectedPeriod === 'publicationperiod') {
        //Add 7 days as default for publishperiod
        if (days < this.config_MaxReachDays - 7) {
          maxDate = addDays(maxDate, 7);
        } else {
          maxDate = dateToyymmddFormat(maxDate);
        }
      }

      if (this.getSelectedPeriod === 'publicationperiod') {
        //Check maxdate from systemInfo
        if (maxDate > this.$store.state.systemInfo.publishedToDate) {
          maxDate = this.$store.state.systemInfo.publishedToDate;
        }
        minDate = dateToyymmddFormat(minDate);
      }

      const titleCodes = this.items
        .filter(e => e.valda)
        .map(e => e.title_code);

      const updates = [
        ['reachApiBody/updateAutoChangeEndDate', updateAutoChangeEndDate],
        ['reachApiBody/updateTitleCodes', titleCodes],
        ['reachApiBody/updateStartDate', minDate],
        ['reachApiBody/updateEndDate', maxDate],
        ['consumptionApiBody/updateAutoChangeEndDate', updateAutoChangeEndDate],
        ['consumptionApiBody/updateTitleCodes', titleCodes],
        ['consumptionApiBody/updateStartDate', minDate],
        ['consumptionApiBody/updateEndDate', maxDate]
      ];

      updates.forEach(([mutation, value]) => {
        this.$store.commit(mutation, value);
      });
    },
  },
};
</script>
<style scoped>
.totalRowsStyle {
  margin-left: 10px;
  margin-top: 8px;
}
.btn-xs span {
  font-size: 0.8em;
}
.pagination {
  justify-content: center;
}

</style>
